'use client';

import React from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { FaChevronLeft, FaChevronRight } from '@/components/icons';
import { COLORS } from '@/constants/colors';


const VIDEOS = [
  { id: 0, videoId: 'qciHtf43Jhg', title: 'Green Filter Video 1' },
  { id: 1, videoId: 'om0RyH2ZXr8', title: 'Green Filter Video 2' },
  { id: 2, videoId: 'LOgLMW-Bdh4', title: 'Green Filter Video 3' },
  { id: 3, videoId: 'd7rETWxgZ8U', title: 'Green Filter Video 4' },
];

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 1200 },
    items: 4,
  },
  desktop: {
    breakpoint: { max: 1200, min: 992 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 992, min: 640 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 640, min: 0 },
    items: 1,
  },
};

interface ArrowProps {
  onClick?: () => void;
}

const CustomLeftArrow: React.FC<ArrowProps> = ({ onClick }) => (
  <LeftArrow onClick={onClick}>
    <FaChevronLeft />
  </LeftArrow>
);

const CustomRightArrow: React.FC<ArrowProps> = ({ onClick }) => (
  <RightArrow onClick={onClick}>
    <FaChevronRight />
  </RightArrow>
);

const HomeElement5 = () => {
  return (
    <Section>
      <Container>
        <HeadingBlock>
          <HeadingRow>
            <Headline>Watch Our Latest Videos</Headline>
            <YoutubeBadge>
              <Image src="/assets/yt.webp" alt="YouTube" width={475} height={106} />
            </YoutubeBadge>
          </HeadingRow>
          <Accent />
        </HeadingBlock>

        <CarouselWrapper>
          <Carousel
            responsive={responsive}
            infinite={true}
            autoPlay={false}
            showDots={false}
            customLeftArrow={<CustomLeftArrow />}
            customRightArrow={<CustomRightArrow />}
            containerClass="carousel-container"
            itemClass="carousel-item"
          >
            {VIDEOS.map((video) => (
              <VideoCard key={video.id}>
                <VideoWrapper>
                  <iframe
                    src={`https://www.youtube.com/embed/${video.videoId}`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </VideoWrapper>
              </VideoCard>
            ))}
          </Carousel>
        </CarouselWrapper>
      </Container>
    </Section>
  );
};

export default HomeElement5;

const Section = styled.section`
  background: #fafafa;
  padding: 80px 24px;

  @media (max-width: 1024px) {
    padding: 64px 24px;
  }

  @media (max-width: 767px) {
    padding: 48px 16px;
  }
`;

const Container = styled.div`
  max-width: 1240px;
  margin: 0 auto;
`;

const HeadingBlock = styled.div`
  max-width: 640px;
  margin: 0 auto 56px;
  text-align: center;

  @media (max-width: 767px) {
    margin-bottom: 40px;
  }
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const Headline = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const YoutubeBadge = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 767px) {
    width: 28px;
    height: 28px;
  }
`;

const Accent = styled.div`
  width: 56px;
  height: 3px;
  background: ${COLORS.brandGreen};
  border-radius: 2px;
  margin: 0 auto;
`;

const CarouselWrapper = styled.div`
  position: relative;

  .carousel-container {
    padding: 0 48px;

    @media (max-width: 767px) {
      padding: 0 40px;
    }
  }

  .carousel-item {
    padding: 0 12px;

    @media (max-width: 767px) {
      padding: 0 8px;
    }
  }
`;

const VideoCard = styled.div`
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  color: #fff;
  font-size: 16px;

  &:hover {
    background: ${COLORS.brandGreen};
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 767px) {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
`;

const LeftArrow = styled(ArrowButton)`
  left: 0;
`;

const RightArrow = styled(ArrowButton)`
  right: 0;
`;
